import { useEffect, useRef, useState } from 'react'

interface Position {
  x: number
  y: number
}

interface UseDraggableOptions {
  /** Clé localStorage pour persister la position entre sessions. */
  storageKey: string
  /** Position par défaut quand rien en localStorage (pixels top-left). */
  defaultPosition: Position | (() => Position)
  /** Marge minimale entre le widget et les bords du viewport. */
  edgePadding?: number
  /** Si false, le drag est désactivé (ex: viewport mobile). Default: drag actif sur >= md. */
  enabled?: boolean
}

interface DragMeta {
  startX: number
  startY: number
  origX: number
  origY: number
  width: number
  height: number
  moved: boolean
}

/**
 * Hook pour rendre un élément déplaçable via pointer events (souris + tactile).
 *
 * - Persiste la position dans localStorage (clé `storageKey`).
 * - Clamp dans le viewport avec marge `edgePadding`.
 * - Recalibre sur resize.
 * - Expose `didDrag` (true si pointer a bougé > 5px depuis pointerdown) pour
 *   permettre aux composants d'inhiber leur onClick natif après un drag.
 *
 * Usage :
 *   const { position, isDragging, didDrag, onPointerDown } = useDraggable({...})
 *   <div style={{ left: position.x, top: position.y }} data-drag-root>
 *     <div onPointerDown={onPointerDown}>handle</div>
 *   </div>
 */
export function useDraggable({
  storageKey,
  defaultPosition,
  edgePadding = 8,
  enabled = true,
}: UseDraggableOptions) {
  const [position, setPosition] = useState<Position>(() =>
    readStoredPosition(storageKey) ??
    (typeof defaultPosition === 'function' ? defaultPosition() : defaultPosition),
  )
  const [isDragging, setIsDragging] = useState(false)
  const [didDrag, setDidDrag] = useState(false)
  const dragRef = useRef<DragMeta | null>(null)

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (!enabled) return
    // Ignore les clics qui ne sont pas un bouton primaire de souris
    if (e.button !== undefined && e.button !== 0) return

    // L'élément racine du widget : on cherche [data-drag-root] sur l'arbre parent
    const root = (e.currentTarget.closest('[data-drag-root]') ||
      e.currentTarget) as HTMLElement
    const rect = root.getBoundingClientRect()
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: position.x,
      origY: position.y,
      width: rect.width,
      height: rect.height,
      moved: false,
    }
    setIsDragging(true)
    setDidDrag(false)
  }

  // Listeners globaux pendant le drag
  useEffect(() => {
    if (!isDragging) return

    const handleMove = (e: PointerEvent) => {
      const d = dragRef.current
      if (!d) return
      const dx = e.clientX - d.startX
      const dy = e.clientY - d.startY
      if (!d.moved && Math.hypot(dx, dy) > 5) {
        d.moved = true
        setDidDrag(true)
      }
      const nextX = clamp(
        d.origX + dx,
        edgePadding,
        window.innerWidth - d.width - edgePadding,
      )
      const nextY = clamp(
        d.origY + dy,
        edgePadding,
        window.innerHeight - d.height - edgePadding,
      )
      setPosition({ x: nextX, y: nextY })
    }

    const handleUp = () => {
      setIsDragging(false)
      dragRef.current = null
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
    }
  }, [isDragging, edgePadding])

  // Persistance — uniquement quand on lâche, pour éviter 60fps writes
  useEffect(() => {
    if (isDragging) return
    try {
      localStorage.setItem(storageKey, JSON.stringify(position))
    } catch {
      // ignore quota errors
    }
  }, [isDragging, position, storageKey])

  // Re-clamp sur resize de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setPosition((p) => ({
        x: Math.min(p.x, Math.max(edgePadding, window.innerWidth - 80)),
        y: Math.min(p.y, Math.max(edgePadding, window.innerHeight - 40)),
      }))
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [edgePadding])

  return { position, isDragging, didDrag, onPointerDown }
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min
  return Math.max(min, Math.min(max, value))
}

function readStoredPosition(key: string): Position | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'x' in parsed &&
      'y' in parsed &&
      typeof (parsed as Position).x === 'number' &&
      typeof (parsed as Position).y === 'number'
    ) {
      return parsed as Position
    }
  } catch {
    /* corrupted JSON — ignore */
  }
  return null
}
